/**
 * Rasterize PDF pages and grab 3D-viewer canvases for inspiration-board snapshots.
 */

import { pdfjs } from 'react-pdf';

const PDF_RENDER_SCALE = 2;
const PDF_THUMB_SCALE = 0.28;

let workerConfigured = false;

function ensurePdfWorker(): void {
  if (workerConfigured) return;
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  workerConfigured = true;
}

const pdfCache = new Map<string, ReturnType<typeof pdfjs.getDocument>['promise']>();

export async function loadPdfDocument(url: string) {
  ensurePdfWorker();
  const key = url.trim();
  let pending = pdfCache.get(key);
  if (!pending) {
    pending = pdfjs.getDocument(key).promise;
    pdfCache.set(key, pending);
    pending.catch(() => {
      pdfCache.delete(key);
    });
  }
  return pending;
}

export async function getPdfPageCount(url: string): Promise<number> {
  const pdf = await loadPdfDocument(url);
  return pdf.numPages;
}

async function renderPdfPageToCanvas(
  url: string,
  pageNumber: number,
  scale: number,
): Promise<HTMLCanvasElement> {
  const pdf = await loadPdfDocument(url);
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create canvas context.');
  await page.render({ canvasContext: ctx, viewport, canvas }).promise;
  return canvas;
}

function canvasToJpegFile(canvas: HTMLCanvasElement, fileName: string, quality = 0.88): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Could not encode snapshot.'));
          return;
        }
        resolve(new File([blob], fileName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      quality,
    );
  });
}

export async function renderPdfPageToFile(
  url: string,
  pageNumber: number,
  fileName: string,
): Promise<File> {
  const canvas = await renderPdfPageToCanvas(url, pageNumber, PDF_RENDER_SCALE);
  return canvasToJpegFile(canvas, fileName);
}

export async function renderPdfPageThumbUrl(url: string, pageNumber: number): Promise<string> {
  const canvas = await renderPdfPageToCanvas(url, pageNumber, PDF_THUMB_SCALE);
  return canvas.toDataURL('image/jpeg', 0.72);
}

function canvasVariance(source: HTMLCanvasElement): number {
  const w = Math.min(64, source.width);
  const h = Math.min(64, source.height);
  if (w < 2 || h < 2) return 0;
  const tmp = document.createElement('canvas');
  tmp.width = w;
  tmp.height = h;
  const ctx = tmp.getContext('2d');
  if (!ctx) return 0;
  try {
    ctx.drawImage(source, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    let min = 255;
    let max = 0;
    for (let i = 0; i < data.length; i += 4) {
      const v = (data[i] + data[i + 1] + data[i + 2]) / 3;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return max - min;
  } catch {
    return -1;
  }
}

function pickLargestCanvas(root: Document | HTMLElement): HTMLCanvasElement | null {
  const canvases = Array.from(root.querySelectorAll('canvas'));
  if (canvases.length === 0) return null;
  return canvases.reduce((best, el) =>
    el.width * el.height > best.width * best.height ? el : best,
  );
}

/** Grab the largest canvas inside a same-origin iframe (3D HTML viewers). */
export async function captureIframeCanvasToFile(
  iframe: HTMLIFrameElement,
  fileName: string,
): Promise<File | null> {
  let doc: Document | null = null;
  try {
    doc = iframe.contentDocument;
  } catch {
    return null;
  }
  if (!doc) return null;

  const canvas = pickLargestCanvas(doc);
  if (!canvas || canvas.width < 8 || canvas.height < 8) return null;

  const variance = canvasVariance(canvas);
  if (variance >= 0 && variance < 6) return null;

  try {
    return await canvasToJpegFile(canvas, fileName);
  } catch {
    return null;
  }
}
